"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SHOWREEL_PHOTOS } from "./data";

// Ancho de la foto central; las vecinas se asoman a los lados.
const SLIDE_W = "w-[78%] md:w-[60%] lg:w-[52%]";
// Cuántas fotos se montan a cada lado de la central (las de ±2 quedan ocultas
// y sirven para que la siguiente entre animada en lugar de aparecer de golpe).
const RANGE = 2;
const SIDE_SCALE = 0.86;
const SIDE_OPACITY = 0.4;
// Distancia entre centros de fotos, en % del ancho de la foto (sin el gap):
// media foto central + media foto lateral ya escalada.
const STEP_PCT = (0.5 + SIDE_SCALE / 2) * 100;
// Arrastre mínimo (en fracciones de paso) para cambiar de foto al soltar.
const DRAG_THRESHOLD = 0.15;
// A partir de cuántos px se considera arrastre y no clic.
const DRAG_START_PX = 6;
// Cada cuánto avanza solo. El avance lo dispara el fin de la animación de la
// barrita activa, así barra y cambio de foto van siempre sincronizados (y con
// prefers-reduced-motion no hay animación, por lo tanto no hay autoplay).
const AUTOPLAY_MS = 5000;

const mod = (n: number, m: number) => ((n % m) + m) % m;
const pad = (n: number) => String(n).padStart(2, "0");

type Drag = { pointerId: number; startX: number; stepPx: number; active: boolean };

const Showreel: React.FC = () => {
  // Posición "virtual" sin límite: cada slide se identifica por su posición y
  // no por la foto, así al avanzar todas se desplazan en la misma dirección
  // aunque la galería dé la vuelta.
  const [pos, setPos] = useState(0);
  // Desplazamiento del arrastre en curso, en fracciones de paso (−1 = una foto).
  const [dragFrac, setDragFrac] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<Drag | null>(null);
  const justDragged = useRef(false);
  const sizerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [inView, setInView] = useState(false);
  const paused = hovered || focused || dragging || !inView;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      threshold: 0.5,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const total = SHOWREEL_PHOTOS.length;
  const active = mod(pos, total);

  const prev = () => setPos((p) => p - 1);
  const next = () => setPos((p) => p + 1);
  const goTo = (i: number) => {
    const half = Math.floor(total / 2);
    setPos((p) => p + mod(i - mod(p, total) + half, total) - half);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const width = sizerRef.current?.offsetWidth ?? 0;
    const gap = parseFloat(getComputedStyle(e.currentTarget).getPropertyValue("--gap")) || 0;
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      stepPx: (width * STEP_PCT) / 100 + gap,
      active: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    if (!d.active) {
      if (Math.abs(dx) < DRAG_START_PX) return;
      // Recién aquí capturamos el puntero: un clic sin movimiento sigue
      // llegando a su botón o foto lateral.
      d.active = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    }
    setDragFrac(Math.max(-1.5, Math.min(1.5, dx / d.stepPx)));
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (!d.active) return;

    const frac = (e.clientX - d.startX) / d.stepPx;
    let steps = -Math.round(frac);
    if (steps === 0 && Math.abs(frac) > DRAG_THRESHOLD) steps = frac < 0 ? 1 : -1;
    steps = Math.max(-1, Math.min(1, steps));

    // Si se suelta fuera de la galería no llega ningún clic: se limpia solo.
    justDragged.current = true;
    setTimeout(() => (justDragged.current = false), 0);
    setPos((p) => p + steps);
    setDragFrac(0);
    setDragging(false);
  };

  const onClickCapture = (e: React.MouseEvent) => {
    // El clic que sigue a un arrastre no debe navegar ni abrir nada.
    if (justDragged.current) {
      justDragged.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") prev();
    else if (e.key === "ArrowRight") next();
  };

  const slides = [];
  for (let offset = -RANGE; offset <= RANGE; offset++) {
    const v = pos + offset;
    const photo = SHOWREEL_PHOTOS[mod(v, total)];
    // Posición continua (incluye el arrastre) para que escala y opacidad
    // sigan al dedo en lugar de saltar al soltar.
    const o = offset + dragFrac;
    const dist = Math.abs(o);
    const scale = 1 - (1 - SIDE_SCALE) * Math.min(dist, 1);
    const opacity = dist <= 1 ? 1 - (1 - SIDE_OPACITY) * dist : Math.max(0, SIDE_OPACITY * (2 - dist));
    const isActive = offset === 0;
    const isSide = Math.abs(offset) === 1;

    slides.push(
      <div
        key={v}
        aria-hidden={!isActive}
        className={`absolute top-0 left-1/2 h-full overflow-hidden bg-black ${SLIDE_W} ${
          dragging ? "" : "transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
        } ${isActive ? "z-10" : ""} ${isActive || isSide ? "" : "pointer-events-none"}`}
        style={{
          transform: `translateX(calc(-50% + ${o} * (${STEP_PCT}% + var(--gap)))) scale(${scale})`,
          opacity,
        }}
      >
        <Image
          src={photo.src}
          alt={photo.alt}
          fill
          draggable={false}
          priority={pos === 0 && isActive}
          sizes="(max-width: 768px) 78vw, 60vw"
          style={{ objectPosition: photo.focus ?? "center" }}
          className={photo.fit === "contain" ? "object-contain" : "object-cover"}
        />
        {isSide && (
          <button
            type="button"
            tabIndex={-1}
            onClick={offset < 0 ? prev : next}
            className="absolute inset-0"
          />
        )}
      </div>,
    );
  }

  return (
    <section className="bg-brand-ink text-white px-4 md:px-8 pt-6 md:pt-10 pb-16 md:pb-24">
      <div className="flex flex-wrap items-center gap-x-[0.35em] text-[clamp(1.4rem,7vw,6.5rem)]">
        <h2 className="mes-display text-[clamp(1.4rem,7vw,6.5rem)] leading-none">Resumen</h2>
        <span className="mes-display text-[clamp(1.4rem,7vw,6.5rem)] leading-none">2026</span>
      </div>

      {/* Pausa el autoplay mientras el mouse está encima, hay foco de teclado
          dentro o la galería no está a la vista. */}
      <div
        ref={rootRef}
        onPointerEnter={(e) => e.pointerType === "mouse" && setHovered(true)}
        onPointerLeave={(e) => e.pointerType === "mouse" && setHovered(false)}
        onFocus={(e) => setFocused(e.target.matches(":focus-visible"))}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
        }}
      >
        <div
          role="region"
          aria-roledescription="carrusel"
          aria-label="Fotos del resumen 2026"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onClickCapture={onClickCapture}
          className={`relative -mx-4 md:-mx-8 mt-10 md:mt-16 overflow-hidden select-none touch-pan-y outline-none [--gap:10px] md:[--gap:20px] ${
            dragging ? "cursor-grabbing" : "cursor-grab"
          }`}
        >
          {/* Da la altura al carrusel según el ancho de la foto central. */}
          <div ref={sizerRef} className={`mx-auto aspect-video ${SLIDE_W}`} />
          {slides}
        </div>

        {/* Mismo sangrado que el carrusel para que la barra calce con la foto central. */}
        <div className="-mx-4 md:-mx-8">
          <div className={`mx-auto mt-4 md:mt-5 flex items-center gap-4 md:gap-6 ${SLIDE_W}`}>
            <div className="flex flex-1 gap-1.5">
              {SHOWREEL_PHOTOS.map((photo, i) => (
                <button
                  key={photo.src}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`Ir a la foto ${i + 1}`}
                  aria-current={i === active}
                  className="group flex h-10 flex-1 items-center"
                >
                  <span
                    className={`relative h-[2px] w-full overflow-hidden transition-colors ${
                      i === active ? "bg-white/25" : "bg-white/25 group-hover:bg-white/50"
                    }`}
                  >
                    {i === active && (
                      <span
                        key={pos}
                        className="mes-showreel-fill absolute inset-0 origin-left bg-white"
                        style={{
                          animationDuration: `${AUTOPLAY_MS}ms`,
                          animationPlayState: paused ? "paused" : "running",
                        }}
                        onAnimationEnd={next}
                      />
                    )}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex shrink-0 items-center gap-3">
              <span className="mes-label tabular-nums text-white/60" aria-live={paused ? "polite" : "off"}>
                <span className="text-white">{pad(active + 1)}</span> / {pad(total)}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Foto anterior"
                  className="flex h-10 w-10 items-center justify-center border border-white/40 transition hover:border-white hover:bg-white hover:text-brand-ink"
                >
                  <ChevronLeft className="h-5 w-5" strokeWidth={1.8} />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Siguiente foto"
                  className="flex h-10 w-10 items-center justify-center border border-white/40 transition hover:border-white hover:bg-white hover:text-brand-ink"
                >
                  <ChevronRight className="h-5 w-5" strokeWidth={1.8} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Showreel;
